import type { Challenge, CourseWithTerm } from "@/lib/api";

/**
 * Why a course counts as "past" on the home page and starts collapsed.
 *
 * Terms only carry a start date, so "over" has to be inferred: the admin
 * archived the term, a newer term has since begun, or every challenge in the
 * course has closed.
 */
export type PastReason = "term-ended" | "previous-term" | "all-closed";

export const PAST_LABEL: Record<PastReason, string> = {
  "term-ended": "Term ended",
  "previous-term": "Previous term",
  "all-closed": "All closed",
};

/** Start of the most recent term that has already begun, or null if none has. */
export function currentTermStartOf(
  courses: readonly CourseWithTerm[],
  now: number,
): number | null {
  let latest: number | null = null;
  for (const course of courses) {
    const start = course.term.starts_at;
    if (start <= now && (latest === null || start > latest)) latest = start;
  }
  return latest;
}

export function pastReason(
  course: CourseWithTerm | undefined,
  challenges: readonly Challenge[],
  currentTermStart: number | null,
  now = Date.now(),
): PastReason | null {
  if (course) {
    if (!course.term.active) return "term-ended";
    if (currentTermStart !== null && course.term.starts_at < currentTermStart) {
      return "previous-term";
    }
  }
  if (
    challenges.length > 0 &&
    challenges.every((c) => c.deadline !== null && c.deadline < now)
  ) {
    return "all-closed";
  }
  return null;
}
